import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  getMostAccessedCatalogs,
  GetMostAccessedCatalogsField,
  GetMostAccessedCatalogsParams,
} from '../../../../apis/relevant-catalog';
import { Catalog } from '../../../../apis/search-catalog';
import { SequentialSource } from '../../../sequential-sources';

export const DEFAULT_PAGE_SIZE = 20;

export type UseMostAccessedCatalogsSourceParams = Omit<
  GetMostAccessedCatalogsParams,
  'field' | 'q'
> & {
  query: string;
};

type UseMostAccessedCatalogsSourceOptions = {
  enabled?: boolean;
};

export const useMostAccessedCatalogsSource = (
  field: GetMostAccessedCatalogsField,
  params: UseMostAccessedCatalogsSourceParams,
  { enabled = true }: UseMostAccessedCatalogsSourceOptions = {}
): SequentialSource<Catalog> => {
  const fetchCatalogs = async ({ pageParam = 0 }) => {
    const { query, limit, offset, ...rest } = params;
    const effectiveLimit = limit ?? DEFAULT_PAGE_SIZE;
    const effectiveOffset = offset ?? pageParam * effectiveLimit;

    const pageData = await getMostAccessedCatalogs({
      field,
      q: query,
      limit: effectiveLimit,
      offset: effectiveOffset,
      ...rest,
    });

    return {
      data: pageData,
      hasMore: pageData.length >= effectiveLimit,
    };
  };

  const {
    data,
    isLoading,
    isSuccess,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['most-accessed-catalogs', field, JSON.stringify(params)],
    queryFn: fetchCatalogs,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) {
        return undefined;
      }

      return allPages.length;
    },
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled && !!params.query && params.query.length > 0,
  });

  const items = useMemo<Catalog[]>(() => {
    return (
      data?.pages?.reduce<Catalog[]>((catalogs, page) => {
        return catalogs.concat(page.data);
      }, []) ?? []
    );
  }, [data]);

  return {
    key: field,
    items,
    hasMore: !!hasNextPage || isLoading,
    isLoading,
    isFetchingMore: isFetchingNextPage,
    isError,
    isSuccess,
    fetchMore: () => {
      if (enabled && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
  };
};
